import { cn } from "../cn";
import { useT } from "../../i18n";
import { goMarketplace, type MarketplaceTab } from "./mkt-routing";

const TABS: { key: MarketplaceTab; labelKey: string; adminOnly?: boolean }[] = [
  { key: "home", labelKey: "marketplace.tabs.overview" },
  { key: "land", labelKey: "marketplace.tabs.land" },
  { key: "artworks", labelKey: "marketplace.tabs.artworks" },
  { key: "mine", labelKey: "marketplace.tabs.mine" },
  { key: "owner", labelKey: "marketplace.tabs.owner", adminOnly: true },
];

export function MarketplaceTabs({
  active,
  isAdmin,
  testId = "marketplace-tabs",
}: {
  active: MarketplaceTab;
  isAdmin: boolean;
  testId?: string;
}) {
  const t = useT();
  // Server-authoritative admin-only tab: for non-admin / anonymous the Owner
  // Console entry is ABSENT from navigation (never a locked/403 tab). Uses the
  // same isAdmin authority (useAdminCapability / /me/capabilities) as the header.
  const tabs = TABS.filter((tab) => !tab.adminOnly || isAdmin);
  return (
    <nav
      data-testid={testId}
      className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
    >
      {tabs.map((tab) => {
        const on = active === tab.key;
        const label = t(tab.labelKey);
        if (tab.key === "owner") {
          // Admin-only surface — canonical coral facet badge, identical chrome to
          // the header ADMIN control (facet-btn--coral).
          return (
            <button
              key={tab.key}
              type="button"
              data-testid={`marketplace-tab-${tab.key}`}
              data-active={on ? "true" : "false"}
              aria-label={t("marketplace.tabs.ownerAria")}
              onClick={() => goMarketplace(tab.key)}
              className={cn(
                "facet-btn facet-btn--coral shrink-0",
                "h-9 px-4 text-[10.5px] font-semibold tracking-[0.18em]",
                on && "shadow-glow-coral-sm",
              )}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path
                  d="M7 1l4.5 1.8v3.4c0 2.9-1.9 4.9-4.5 5.8-2.6-.9-4.5-2.9-4.5-5.8V2.8L7 1z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
              {label}
            </button>
          );
        }
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={`marketplace-tab-${tab.key}`}
            data-active={on ? "true" : "false"}
            onClick={() => goMarketplace(tab.key)}
            className={cn(
              "relative h-9 px-4 rounded-md whitespace-nowrap shrink-0",
              "font-mono text-[10.5px] font-semibold tracking-[0.18em] uppercase",
              "transition-[color,background-color] duration-fast",
              on
                ? "text-neon-teal bg-neon-teal/10"
                : "text-text-secondary hover:text-text-primary hover:bg-elevated/60",
            )}
          >
            {label}
            {on && (
              <span aria-hidden className="absolute left-3 right-3 -bottom-px h-px bg-neon-teal shadow-glow-teal-sm" />
            )}
          </button>
        );
      })}
    </nav>
  );
}
